<template>
  <div class="pa-4">
    <v-row justify="space-between">
      <v-col v-if="!noHeader">
        <div class="font-weight-bold basic--text text-center">{{ 'Nysc Details'.toUpperCase() }}</div>
      </v-col>
    </v-row>
    <v-form ref="form" v-model="dataValid">
      <v-alert type="warning" class="mt-4" v-if="!noHeader">
        Make sure to provide correct information as editing will not be allowed after submission
      </v-alert>
      <v-row class="pt-8">
        <v-col cols="12" md="4">
          <v-select :items="countries" item-text="name" item-value="id" outlined dense label="Country" required v-model="selectedCountry" :rules="textRules" :disabled="!canEdit"></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select :items="states" item-text="name" item-value="id" outlined dense label="State" required v-model="obj.stateId" :rules="textRules" :disabled="!canEdit"></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select dense outlined placeholder="Date" :items="dates" v-model="obj.year" required :rules="textRules" :disabled="!canEdit"></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field outlined dense label="Address" required :rules="textRules" v-model="obj.location" :disabled="!canEdit"></v-text-field>
        </v-col>
      </v-row>
    </v-form>

    <!-- File Upload Section - Shows after NYSC details are saved -->
    <v-card v-if="showFileUpload" class="mt-6">
      <v-card-title class="pb-2">
        <span class="text-h6">NYSC Certificate Upload</span>
      </v-card-title>
      <v-card-text>
        <!-- Show uploaded file info if exists -->
        <div v-if="certificateStatus && certificateStatus.hasUploaded" class="mb-4">
          <v-alert type="success" class="mb-3">
            <v-row align="center" no-gutters>
              <v-col>
                <strong>Certificate uploaded successfully!</strong><br>
                <span class="text-caption">{{ uploadedFileInfo.filename || 'NYSC Certificate' }}</span>
              </v-col>
              <v-col cols="auto">
                <v-btn small outlined color="secondary" @click="downloadCertificate" class="mr-2">
                  <v-icon small left>mdi-download</v-icon>
                  Download
                </v-btn>
                <v-btn small outlined color="warning" @click="showReplaceUpload = true">
                  <v-icon small left>mdi-upload</v-icon>
                  Replace
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </div>

        <!-- Upload interface -->
        <div v-if="!certificateStatus?.hasUploaded || showReplaceUpload">
          <v-alert type="info" class="mb-4">
            Upload your NYSC discharge certificate or exemption letter<br>
            <strong>Accepted formats:</strong> PDF, JPG, JPEG, PNG<br>
            <strong>Maximum file size:</strong> 5MB
          </v-alert>
          <v-file-input
            v-model="selectedFile"
            label="Select NYSC Certificate"
            outlined
            dense
            accept=".pdf,.jpg,.jpeg,.png"
            :rules="fileRules"
            prepend-icon="mdi-file-document-outline"
            show-size
          ></v-file-input>
        </div>
      </v-card-text>
      <v-card-actions v-if="!certificateStatus?.hasUploaded || showReplaceUpload">
        <v-spacer></v-spacer>
        <v-btn v-if="showReplaceUpload" color="basic" text @click="cancelReplace">
          Cancel
        </v-btn>
        <v-btn color="primary" depressed @click="uploadFile" :disabled="!selectedFile || uploading" :loading="uploading">
          {{ showReplaceUpload ? 'Replace Certificate' : 'Upload Certificate' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card-actions class="mt-6">
      <v-spacer></v-spacer>
      <v-btn color="basic" class="white--text" @click="$emit('back')" depressed>{{ noHeader ? 'Back' : 'Previous' }}</v-btn>
      <v-btn color="error" depressed @click="$refs.form.reset()" v-if="userData == null && canEdit && !noHeader">Reset</v-btn>
      <v-btn color="primary" depressed @click="saveData" :disabled="!dataValid" v-if="canEdit">{{ noHeader ? 'Save' : 'Proceed' }}</v-btn>
      <v-btn color="info" depressed @click="success" v-if="(userData != null || !canEdit) && certificateStatus?.hasUploaded">Skip</v-btn>
    </v-card-actions>
    <loading-dialog :show="loading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType" v-on:close-message="messageShow = !messageShow"></message-box>
  </div>

</template>


<script>
import DateUtil from "@/shared/DateUtil";
import ValidationRules from "@/shared/ValidationRules";
import {mapGetters} from "vuex";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import StudentStatus from "@/shared/StudentStatus";

export default {
  name: "NyscDetails",
  props: {
    noHeader: Boolean
  },
  components: {MessageBox, LoadingDialog},
  data: () => ({
    obj: {},
    dates: DateUtil.getYearsArray(1970),
    textRules: ValidationRules.textRules,
    states: [],
    countries: [],
    selectedCountry: null,
    dataValid: true,
    userData: null,
    messageType: '',
    message: '',
    messageShow: false,
    loading: false,
    selectedFile: null,
    uploading: false,
    certificateStatus: null,
    uploadedFileInfo: null,
    showReplaceUpload: false,
    fileRules: [
      v => !!v || 'Please select a file to upload',
      v => !v || v.size < 5000000 || 'File size exceeds the maximum limit',
      v => !v || ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(v.type) || 'Invalid file type. Only PDF, JPG, JPEG, and PNG files are allowed'
    ],
  }),

  watch: {
    userData: function (val){
      if(val != null){
        // this.success()
      }
    },

    getUser: function(){
      if(this.checkRequirement()) {
        this.success()
        return
      }

      if(this.getUser != null)
        this.loadNYCEDetails()
    },

    selectedCountry: function(newVal){
      if(newVal != null){
        this.selectedState = null
        this.states = []
        this.loadStates()
      }
    },
  },

  computed: {
    ...mapGetters(['getUser']),
    canEdit: function(){
      // status still not upto application complete
      return this.getUser != null && (StudentStatus[this.getUser.studentStatus].value < StudentStatus.APPLICATION_COMPLETE.value)
    },
    showFileUpload: function(){
      // Show file upload if NYSC details are required, exist, but no document has been uploaded
      const nyscDetailsEnabled = this.getUser?.admission?.applicationType?.nyscDetailsEnabled
      return nyscDetailsEnabled && this.userData != null && (this.userData.state )
    }
  },

  methods: {
    success() {
      this.$emit("success")
      // this.$router.push('dashboard')
    },

    checkRequirement(){
      return this.getUser != null && this.getUser.admission.applicationType.nyscDetailsEnabled === false
    },

    loadNYCEDetails(){
      this.loading = true;
      Api.applicants.getNYCEDetails(this.getUser.userId)
          .then(res => {
            this.userData = res.data
            this.prefillData()
            this.checkCertificateStatus()
          })
          .catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
    },

    check(){
      Api.applicants.nextToNYSCUpdate(this.getUser.id).then((res) => {
        this.loading = true
        this.$store.commit("setCurrentUser", res.data);
        this.loading = false;
      }).catch((err) => ErrorResolver.checkErrors(this, err))
      .finally(() => this.loading = false )
    },

    saveData(){
      this.loading = true
      Api.applicants.nyscUpdate(this.getUser.id, this.obj)
          .then(() => {
            this.message = 'NYCS details updated'
            this.messageType = 'success'
            this.messageShow = true
            this.loadNYCEDetails() // Reload to show file upload section
          }).catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
    },

    uploadFile(){
      if (!this.selectedFile) return

      this.uploading = true
      const formData = new FormData()
      formData.append('file', this.selectedFile)
      formData.append('userId', this.getUser.userId)

      Api.applicants.nyscCertificateUpload(this.getUser.id, formData)
          .then(() => {
            this.message = 'NYSC certificate uploaded successfully'
            this.messageType = 'success'
            this.messageShow = true
            this.selectedFile = null
            this.showReplaceUpload = false
            this.checkCertificateStatus() // Refresh certificate status
            this.loadNYCEDetails() // Reload to refresh data
          }).catch(err => {
            ErrorResolver.checkErrors(this, err)
          })
          .finally(() => this.uploading = false)
    },

    checkCertificateStatus(){
      if (!this.getUser?.id) return

      Api.applicants.nyscCertificateStatus(this.getUser.id)
          .then(res => {
            this.certificateStatus = res.data
            if (res.data.hasUploaded && res.data.nyscDetails) {
              this.uploadedFileInfo = {
                filename: 'NYSC Certificate',
                uploadDate: new Date().toLocaleDateString()
              }
            }
          })
          .catch(err => {
            // Silently handle error - certificate status is optional
            console.log('Certificate status check failed:', err)
          })
    },

    downloadCertificate(){
      Api.applicants.nyscCertificateDownload(this.getUser.id)
          .then(res => {
            let fileURL = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            window.open(fileURL, '_blank');
          })
          .catch(err => ErrorResolver.checkErrors(this, err))
    },

    cancelReplace(){
      this.showReplaceUpload = false
      this.selectedFile = null
    },

    prefillData(){
      if(this.userData != null){
        let u = this.userData
        if(u.state != null){
          this.obj.stateId = u.state.id
          this.selectedCountry = u.state.country.id
        }

        this.obj.location = u.location
        this.obj.year = +u.year
      }
    },

    loadCountries(){
      this.loading = true
      Api.nationality.getCountries()
          .then(res => {
            this.countries = res.data
          })
          .catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
    },

    loadStates(){
      this.loading = true
      this.states = []
      Api.nationality.getStates(this.selectedCountry)
          .then(res => {
            this.states = res.data
            // update state object
            if(this.getInformation != null)
              this.selectedState = this.getInformation.state.id
          })
          .catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
    },
  },

  created() {
    this.loadCountries()
    if(this.getUser != null){
      if(this.checkRequirement()){
        this.success()
        return
      }

      else {
        this.check();
        this.loadNYCEDetails();
      }

    }
    this.noHeader && this.$emit('update-title', "NYSC Details")
  }
}
</script>
